import { DSFlex, DSSpacer, DSSurfaceColorType, DSText } from '@zoe/ds-web'
import React from 'react'

import { FragmentComponent } from '@/types/graphql'

import { DSGrid, DSGridBlock } from '@/components/ds/Grid/Grid'

import { ctaCollectionBlock } from '@/lib/api/graphql/utils/commonBlocks'

import { processTypographyMarkdown } from '../DesignSystem/Typography'
import { CtaCollectionBlock, CtaCollectionRecord } from './CtaCollectionBlock'
import { FeatureGridBlock, FeatureRecord } from './FeatureGridBlock'

export type FeatureGridSectionBlockRecord = {
  __typename: 'FeatureGridSectionBlockRecord'
  id: string
  eyebrow?: string
  title?: string
  subtitle?: string
  surfaceColor?: DSSurfaceColorType
  ctaCollection?: CtaCollectionRecord
  featureGrid?: FeatureRecord[]
  dataTestId?: string
}

const fragment = `
  fragment FeatureGridSectionBlockFragment on FeatureGridSectionRecord {
    __typename
    id
    eyebrow
    title
    subtitle
    ${FeatureGridBlock.fragment}
    ${ctaCollectionBlock}
    surfaceColor
    dataTestId
  }
`

export const FeatureGridSectionBlock: FragmentComponent<{}, FeatureGridSectionBlockRecord> = ({ record }) => {
  const typographyColor = ['navy-bold', 'aubergine-bold', 'teal-bold'].includes(record.surfaceColor || '')
    ? 'primary-inverse'
    : 'primary'

  return (
    <DSGrid
      backgroundColor={record.surfaceColor || 'neutral'}
      mobileBackgroundColor={record.surfaceColor || 'neutral'}
      backgroundFullWidth
    >
      <DSSpacer direction="vertical" size={144} breakAt={{ m: 96 }} />
      <DSGridBlock
        dataTestId={record?.dataTestId}
        padded
        textAlign="center"
        breakConfig={{
          s: { colSpan: 2 },
          m: { colSpan: 6 },
          l: { colSpan: 10, offset: 1 },
          xl: { colSpan: 10, offset: 1 },
        }}
      >
        {record.eyebrow && (
          <>
            <DSText as="span" variant="fluid-eyebrow-100" weight="semiBold" align="center" color={typographyColor}>
              {record.eyebrow}
            </DSText>
            <DSSpacer direction="vertical" size={24} />
          </>
        )}
        {record.title && (
          <>
            <DSText as="h4" variant="fluid-heading-900alt" weight="semiBold" align="center" color={typographyColor}>
              {processTypographyMarkdown({ content: record.title, variant: 'fluid-heading-900alt' })}
            </DSText>
            <DSSpacer direction="vertical" size={16} />
          </>
        )}
        {record.subtitle && (
          <>
            <DSText as="h2" variant="fluid-heading-500" weight="light" align="center" color={typographyColor}>
              {processTypographyMarkdown({ content: record.subtitle, variant: 'fluid-heading-500' })}
            </DSText>
            <DSSpacer direction="vertical" size={16} />
          </>
        )}
        {record.featureGrid && (
          <>
            <DSSpacer direction="vertical" size={24} />
            <FeatureGridBlock record={record.featureGrid} typographyColor={typographyColor} />
          </>
        )}
        {record.ctaCollection && (
          <>
            <DSSpacer direction="vertical" size={40} />
            <DSFlex direction="column" gap={16} alignItems="center" justifyContent="center">
              <CtaCollectionBlock record={record.ctaCollection} />
            </DSFlex>
          </>
        )}
      </DSGridBlock>
      <DSSpacer direction="vertical" size={144} breakAt={{ m: 96 }} />
    </DSGrid>
  )
}

FeatureGridSectionBlock.fragment = fragment
FeatureGridSectionBlock.recordName = 'FeatureGridSectionRecord'
